import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const SupplierProductRowSchema = z.object({
  id: z.string().uuid(),
  supplierName: z.string(),
  /** ProductV3 key (the SKU) — the row's real product identity. */
  productV3Key: z.string().nullable(),
  /**
   * Legacy v2 passenger. NULL on a v3-only supplier product, which is why this
   * is nullable — it used to be a required uuid, and that made every v3-keyed
   * row unpublishable.
   */
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  unitPrice: z.number(),
  minimumOrderQuantity: z.number().nullable(),
  incrementalOrderQuantity: z.number().nullable(),
  leadTimeDays: z.number().nullable(),
  priceBreakCount: z.number(),
});

export type SupplierProductRow = z.infer<typeof SupplierProductRowSchema>;

// ─── List ───────────────────────────────────────────────────────────────────

export const ListSupplierProductsInputSchema = z.object({
  warehouseId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
  search: z.string().optional(),
  warehouseNames: z.array(z.string()).optional(),
  supplierNames: z.array(z.string()).optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListSupplierProductsResponseSchema = z.object({
  rows: z.array(SupplierProductRowSchema),
  totalRows: z.number(),
});

export type ListSupplierProductsInput = z.infer<
  typeof ListSupplierProductsInputSchema
>;
export type ListSupplierProductsResponse = z.infer<
  typeof ListSupplierProductsResponseSchema
>;

// ─── Detail (by product) ────────────────────────────────────────────────────

/**
 * Address a product by either key. ProductV3 (`productV3Key`, the SKU) is the
 * authoritative identity; the legacy v2 `productId` is the fallback for rows
 * written before the re-key. Both are optional because which one exists depends
 * on the row — callers must supply at least one.
 */
export const GetSupplierProductDetailInputSchema = z
  .object({
    /** Preferred — the only key that reaches v3-only supplier products. */
    productV3Key: z.string().min(1).nullable().optional(),
    productId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.productV3Key != null || v.productId != null, {
    message: "Supply productV3Key (preferred) or productId.",
  });

const PriceBreakSchema = z.object({
  tier: z.number(),
  minQuantity: z.number(),
  unitCost: z.number(),
});

const SupplierForProductSchema = z.object({
  supplierProductId: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  unitPrice: z.number(),
  minimumOrderQuantity: z.number().nullable(),
  incrementalOrderQuantity: z.number().nullable(),
  leadTimeDays: z.number().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  priceBreaks: z.array(PriceBreakSchema),
});

export const GetSupplierProductDetailResponseSchema = z.object({
  /** ProductV3 key (the SKU) — the product's real identity. */
  productV3Key: z.string().nullable(),
  /** Legacy v2 passenger. NULL when the product exists only in ProductV3. */
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  originId: z.string().nullable(),
  originSystem: z.string().nullable(),
  isActive: z.boolean(),
  suppliers: z.array(SupplierForProductSchema),
});

export type GetSupplierProductDetailInput = z.infer<
  typeof GetSupplierProductDetailInputSchema
>;
export type GetSupplierProductDetailResponse = z.infer<
  typeof GetSupplierProductDetailResponseSchema
>;
export type SupplierForProduct = z.infer<typeof SupplierForProductSchema>;
export type PriceBreak = z.infer<typeof PriceBreakSchema>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const supplierProductsContracts = {
  list: oc
    .input(ListSupplierProductsInputSchema)
    .output(ListSupplierProductsResponseSchema)
    .route({
      method: "GET",
      summary:
        "List supplier product records (supplier × product × warehouse) for the current org.",
    }),
  getByProduct: oc
    .input(GetSupplierProductDetailInputSchema)
    .output(GetSupplierProductDetailResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get all suppliers for a given product, with per-warehouse pricing, MOQ, lead times, and price breaks.",
    }),
} as const;
